import { AlertTriangle, CheckSquare, HelpCircle, ListChecks } from 'lucide-react';
import type { GuidanceDto } from '@ederest/contracts';
import { COMPONENT_LABEL, LANGUAGE_LABEL, isRtl } from './labels.js';

interface BilingualPreviewProps {
  guidance: Pick<GuidanceDto, 'component' | 'contents' | 'reinforced'>;
  languages: readonly string[];
}

/**
 * The rendering in every active language, side by side, before publication
 * (functional §8.4 "Prévisualisation"): the look of the SDK components, native
 * right-to-left for Arabic. A missing text shows as such rather than as an
 * empty box.
 */
export function BilingualPreview({ guidance, languages }: BilingualPreviewProps) {
  return (
    <div className="preview" data-testid="bilingual-preview">
      {languages.map((language) => {
        const content = guidance.contents[language];
        const rtl = isRtl(language);
        return (
          <section key={language} className="preview__pane" data-language={language}>
            <header className="preview__pane-head">
              <span>{LANGUAGE_LABEL[language] ?? language.toUpperCase()}</span>
              <span className="preview__component">{COMPONENT_LABEL[guidance.component]}</span>
            </header>
            <div className={`preview__page ${rtl ? 'preview__page--rtl' : ''}`} dir={rtl ? 'rtl' : 'ltr'}>
              <div className="preview__field" aria-hidden />
              <article className={`preview__bubble preview__bubble--${guidance.component}`}>
                <span className="preview__pointer" aria-hidden />
                <header className="preview__bubble-head">
                  <Icon component={guidance.component} />
                  <strong>{content?.title || <em>Titre à rédiger</em>}</strong>
                </header>
                <p className="preview__body">{content?.body || <em>Contenu à rédiger</em>}</p>
                <Items
                  component={guidance.component}
                  items={content?.items ?? []}
                  reinforced={guidance.reinforced}
                />
              </article>
            </div>
          </section>
        );
      })}
    </div>
  );
}

function Icon({ component }: { component: GuidanceDto['component'] }) {
  if (component === 'warning') return <AlertTriangle size={14} />;
  if (component === 'checklist') return <ListChecks size={14} />;
  if (component === 'guided_tour' || component === 'progress_indicator') return <CheckSquare size={14} />;
  return <HelpCircle size={14} />;
}

function Items({
  component,
  items,
  reinforced,
}: {
  component: GuidanceDto['component'];
  items: readonly string[];
  reinforced: boolean;
}) {
  if (items.length === 0) return null;
  if (component === 'checklist') {
    return (
      <ul className="preview__checklist">
        {items.map((item) => (
          <li key={item}>
            <span className="preview__check" aria-hidden />
            {item}
          </li>
        ))}
      </ul>
    );
  }
  if (component === 'guided_tour') {
    return (
      <ol className="preview__steps">
        {items.map((item, index) => (
          <li key={item} className={index === 0 ? 'preview__step--current' : undefined}>
            {item}
          </li>
        ))}
        <li className="preview__steps-note">
          {reinforced ? 'Une décision par étape' : `Étape 1 sur ${items.length}`}
        </li>
      </ol>
    );
  }
  return (
    <ul className="preview__list">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}
